import React from 'react';

import Button from '@atlaskit/button/standard-button';
import CheckCircle from '@atlaskit/icon/glyph/check-circle';
import Warning from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Expander } from 'src/components/sidebar';
import { Commit } from 'src/components/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';

import { messages } from '../../gpg-keys.i18n';
import { isValidSignature, useVerificationStatus } from '../../utils';
import { GpgKeysUnverifiedCardContent } from '../commit-verification/unverified-card/unverified-card-content';
import { GpgKeysVerifiedCardContent } from '../commit-verification/verified-card/verified-card-content';

export const ExpandGpgKeyCard = ({
  commit,
}: {
  commit: Commit;
}): JSX.Element | null => {
  const intl = useIntl();
  const hasGpgKeyFeature = useStatsigGate(StatsigFeatureKeys.gpgKeysUi);
  const { verification } = useVerificationStatus(commit);

  if (!hasGpgKeyFeature || !verification) {
    return null;
  }

  const commitWithVerification = {
    ...commit,
    extra: {
      ...commit.extra,
      verification,
    },
  };

  const isVerified = isValidSignature(verification.status);
  const label = isVerified
    ? intl.formatMessage(messages.verifiedLabel)
    : intl.formatMessage(messages.unverifiedLabel);
  const icon = isVerified ? (
    <CheckCircle
      label={label}
      primaryColor={token('color.icon.success', colors.G300)}
    />
  ) : (
    <Warning
      label={label}
      primaryColor={token('color.background.warning.bold', colors.Y300)}
    />
  );

  return (
    <Expander label={label} icon={icon}>
      {isVerified ? (
        <GpgKeysVerifiedCardContent commit={commitWithVerification} />
      ) : (
        <GpgKeysUnverifiedCardContent />
      )}
    </Expander>
  );
};

export const CollapseGpgKeyCard = ({
  commit,
}: {
  commit: Commit;
}): JSX.Element | null => {
  const intl = useIntl();
  const hasGpgKeyFeature = useStatsigGate(StatsigFeatureKeys.gpgKeysUi);
  const { verification } = useVerificationStatus(commit);

  if (!hasGpgKeyFeature || !verification) {
    return null;
  }

  const isVerified = true;
  const label = isVerified
    ? intl.formatMessage(messages.verifiedLabel)
    : intl.formatMessage(messages.unverifiedLabel);
  const icon = isVerified ? (
    <CheckCircle
      label={label}
      primaryColor={token('color.icon.success', colors.G300)}
    />
  ) : (
    <Warning
      label={label}
      primaryColor={token('color.background.warning.bold', colors.Y300)}
    />
  );

  return (
    <Tooltip content={label} position="left">
      <Button label={label} appearance="subtle" iconBefore={icon} />
    </Tooltip>
  );
};
